import React, { FormEvent, useEffect, useState } from "react";
import { useToaster } from "contexts/toasterContext";
import apiService from "services/apiService";
import Select from "react-select";
import { customSelectStyles } from "util/selectStyles";

export type HeadType = "Income Head" | "Deduction Head";

export interface AddHeadPayload {
  headType: HeadType;
  accountId?: number;
  accountCode: string;
  headName: string;
}

interface AddHeadFormValues {
  headType: HeadType | "";
  accountCode: string;
  headName: string;
}

interface AddHeadProps {
  onClose: () => void;
  onSave: (payload: AddHeadPayload) => void | Promise<void>;
  initialValues?: Partial<AddHeadPayload>;
}

const headTypeOptions: HeadType[] = ["Income Head", "Deduction Head"];

interface AccountCodeOption {
  id: number;
  accountCode: string;
  disabled?: boolean;
}

interface AccountSelectOption {
  value: string;
  label: string;
  id: number;
}

const AddHead: React.FC<AddHeadProps> = ({
  onClose,
  onSave,
  initialValues,
}) => {
  const { addToast } = useToaster();
  const [formValues, setFormValues] = useState<AddHeadFormValues>({
    headType: initialValues?.headType ?? "",
    accountCode: initialValues?.accountCode ?? "",
    headName: initialValues?.headName ?? "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof AddHeadPayload, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accountCodeOptions, setAccountCodeOptions] = useState<
    AccountCodeOption[]
  >([]);

  const accountSelectOptions: AccountSelectOption[] = accountCodeOptions.map(
    (option) => ({
      value: option.accountCode,
      label: option.accountCode,
      id: option.id,
    }),
  );



  const getErrorMessage = (error: unknown): string => {
    const typedError = error as {
      response?: {
        data?: {
          message?: string;
          error?: string;
          details?: string;
        };
      };
      message?: string;
    };

    return (
      typedError?.response?.data?.message ||
      typedError?.response?.data?.error ||
      typedError?.response?.data?.details ||
      typedError?.message ||
      "Failed to save head"
    );
  };

  const handleChange = (key: keyof AddHeadFormValues, value: string) => {
    if (key === "headName") {
      value = value.replace(/[^a-zA-Z0-9\s,.]/g, "");
    }

    setFormValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key as keyof AddHeadPayload]: "" }));
  };

  useEffect(() => {
    console.log("initialValues changed");
  }, [initialValues]);

  useEffect(() => {
    apiService
      .get("/api/accounts")
      .then((response) => {
        const rawData = response.data as
          | AccountCodeOption[]
          | { data?: AccountCodeOption[] }
          | undefined;
        const records = Array.isArray(rawData)
          ? rawData
          : Array.isArray(rawData?.data)
            ? rawData.data
            : [];

        setAccountCodeOptions(
          records.filter((account) => !account.disabled),
        );
      })
      .catch((error) => {
        console.error("Failed to fetch account codes:", error);
        addToast(getErrorMessage(error), "error");
      });
  }, [addToast]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Partial<Record<keyof AddHeadPayload, string>> = {};

    if (!formValues.headType) {
      nextErrors.headType = "Head Type is required";
    }

    if (!formValues.accountCode) {
      nextErrors.accountCode = "Account Code is required";
    }

    if (!formValues.headName.trim()) {
      nextErrors.headName = "Head Name is required";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      const selectedAccount = accountCodeOptions.find(
        (option) => option.accountCode === formValues.accountCode,
      );

      if (!selectedAccount) {
        setErrors((prev) => ({
          ...prev,
          accountCode: "Please select a valid account code",
        }));
        return;
      }

      await onSave({
        headType: formValues.headType as HeadType,
        accountId: selectedAccount.id,
        accountCode: formValues.accountCode,
        headName: formValues.headName.trim(),
      });
    } catch (error) {
      console.error("Failed to save salary head:", error);
      addToast(getErrorMessage(error), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const headTypeSelectOptions = headTypeOptions.map((option) => ({
    value: option,
    label: option,
  }));

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">
          Head Type<span className="text-red-500">*</span>
        </label>

        <Select
          value={
            headTypeSelectOptions.find(
              (option) => option.value === formValues.headType
            ) || null
          }
          onChange={(selectedOption) =>
            handleChange("headType", selectedOption?.value || "")
          }
          options={headTypeSelectOptions}
          placeholder="Select Head Type"
          menuPlacement="bottom"
          menuPosition="fixed"
          menuPortalTarget={typeof window !== "undefined" ? document.body : null}
          isDisabled={isSubmitting}
          styles={customSelectStyles}
        />

        {errors.headType && (
          <p className="mt-1 text-sm text-red-500">{errors.headType}</p>
        )}
      </div>

      <div>
        <label className="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">
          Account Code <span className="text-red-500">*</span>
        </label>
        <Select
          value={
            accountSelectOptions.find(
              (option) => option.value === formValues.accountCode,
            ) || null
          }
          onChange={(selectedOption) =>
            handleChange("accountCode", selectedOption?.value || "")
          }
          options={accountSelectOptions}
          placeholder={
            accountCodeOptions.length
              ? "Select Account Code"
              : "Loading Account Codes..."
          }
          menuPlacement="bottom"
          menuPosition="fixed"
          menuPortalTarget={typeof window !== "undefined" ? document.body : null}
          isDisabled={isSubmitting}
          // styles={{
          //   control: (base) => ({
          //     ...base,
          //     minHeight: "42px",
          //     borderRadius: "0.5rem",
          //     borderColor: errors.accountCode ? "#ef4444" : "#cbd5e1",
          //     backgroundColor: "var(--tw-bg-opacity)",
          //   }),
          //   menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          //   menu: (base) => ({ ...base, zIndex: 9999 }),
          // }}
          styles={customSelectStyles}
        />
        {errors.accountCode && (
          <p className="mt-1 text-sm text-red-500">{errors.accountCode}</p>
        )}
      </div>

      <div>
        <label className="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">
          Head Name<span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formValues.headName}
          onChange={(event) => handleChange("headName", event.target.value)}
          placeholder="Head Name"
          className={`w-full rounded-lg border bg-white dark:bg-gray-800 px-3 py-2.5 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/40 ${errors.headName
            ? "border-red-500"
            : "border-slate-300 dark:border-slate-700"
            }`}
        />
        {errors.headName && (
          <p className="mt-1 text-sm text-red-500">{errors.headName}</p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="px-5 py-2 rounded-xl text-sm font-semibold bg-[#EEF2F7] text-[#153B6E] dark:bg-[#334860] dark:text-white transition-opacity hover:opacity-90"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          style={{ backgroundColor: "#1d4ed8" }}
          className="px-5 py-2 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
};

export default AddHead;
